import { Box, Flex, TextV2 } from '@withjoy/joykit';
import React, { useMemo } from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { JoyAiV2 } from '@withjoy/joykit/icons';
import { EventVenueState } from '../EventVenueDialog/useEventVenueDialog';
import { useTranslation } from '@shared/core';
import { AiBorderBox } from '@shared/components/AiBorderBox';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { VerticalScrollMessages } from '@shared/components/VerticalScrollMessages';

interface MapToastProps {
  isLoading: boolean;
  eventVenueState: EventVenueState;
  allHotelCount: number;
  bestHotelsCount: number;
}

export const MapToast = ({ isLoading, eventVenueState, allHotelCount, bestHotelsCount }: MapToastProps) => {
  const isMobile = useIsMobileScreen();
  const { tq } = useTranslation('travelMap');

  const shouldShowHotelsMessage = ['intro', 'filters'].includes(eventVenueState) && allHotelCount > 0 && bestHotelsCount > 0;

  let mapToast = tq`Loading...`;
  if (eventVenueState === 'intro') {
    mapToast = tq`We found ${allHotelCount} hotels near your venue!`;
  } else if (eventVenueState === 'filters') {
    mapToast = `We found ${allHotelCount} hotels and selected the best ${bestHotelsCount} for you!`;
  }

  const messages = useMemo(() => {
    return [
      tq`Finding the best and most loved spots near your venue...`,
      tq`Unlocking your exclusive Joy rates...`,
      tq`Creating your personalized 'Where to Stay' guide...`,
      tq`Locking in the best deals for your guests...`,
      tq`No room blocks needed - simple booking...`,
    ];
  }, [tq]);

  if (!isLoading && !shouldShowHotelsMessage) return null;

  if ((!isLoading && shouldShowHotelsMessage) || eventVenueState === 'hotels') {
    return (
      <Box
        position="absolute"
        left="50%"
        top={7}
        transform="translate(-50%, 0)"
        maxWidth={'80%'}
      >
        <AiBorderBox
          background={'rgba(255, 255, 255, 0.96)'}
          showGradientShadow
        >
          <JoyAiV2 size={32} />
          <TextV2
            typographyVariant={'body1'}
            fontSize={pxToRem(13)}
            fontWeight={500}
          >
            {mapToast}
          </TextV2>
        </AiBorderBox>
      </Box>
    );
  }

  return (
    <Box
      position="absolute"
      left="50%"
      transform="translate(-50%, 0)"
      maxWidth={`calc(100% - ${isMobile ? pxToRem(48) : pxToRem(74)})`}
      {...(isMobile ? { top: 6 } : { bottom: pxToRem(42) })}
    >
      <AiBorderBox
        background={'rgba(255,255,255,0.72)'}
        showTransparentBackground
        backdropFilter="blur(8px)"
        borderRadius={16}
        padding={0}
        showShadow
      >
        <Flex
          alignItems={'center'}
          gap={4}
          padding={6}
        >
          <JoyAiV2 size={32} />
          <VerticalScrollMessages
            messages={messages}
            height={pxToRem(144)} // Display at least 3 lines of text (3 × 48px)
          />
        </Flex>
      </AiBorderBox>
    </Box>
  );
};
